import {
    accountActions,
    alertActions,
    coverManagerActions,
    facebookActions,
    googleActions,
    locationsProfilesActions,
    vtexActions,
} from '@actions';
import { ModalConfirm } from '@components/Modal';
import MoreMenu from '@components/MoreMenu';
import Tooltip from '@components/Tooltip';
import { Typography } from '@components/Typography';
import { LocationIntegrationStatus } from '@constants/companies';
import { LocationIntegrationName, ProfileName } from '@constants/integrations';
import { LOCALE } from '@constants/locale';
import { handleApiError } from '@helpers/api';
import { createProxyObject } from '@helpers/createProxyObject';
import { useTranslation } from '@hooks';
import HelpIcon from '@mui/icons-material/Help';
import IconButton from '@mui/material/IconButton';
import TableContainer from '@mui/material/TableContainer';
import { RoutePath } from '@constants/routes';
import { appleIntegrationService, companyService } from '@services';
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ManageProfileModal from '../ManageProfileModal';
import GenericIntegration from './GenericIntegration';
import IFoodIntegration from './IFoodIntegration';
import { Container, Point, StyledTable, TableText } from './styles';

const IntegrationDrawer = props => {
    switch (Number(props.integrationType)) {
        case LocationIntegrationName.iFood:
            return <IFoodIntegration {...props} />;
        case LocationIntegrationName.Google:
        case LocationIntegrationName.Facebook:
        case LocationIntegrationName.VTEX:
        case LocationIntegrationName.CoverManager:
            return <GenericIntegration {...props} />;
        default:
            return null;
    }
};

const getColumnNames = t => [
    {
        key: 0,
        component: <Typography variant="subtitle2">{t('SettingsCompanies.integrationName')}</Typography>,
    },
    {
        key: 1,
        component: (
            <Typography align="left" variant="subtitle2">
                {t('common.status')}
            </Typography>
        ),
    },
    {
        key: 2,
        component: <div />,
    },
];

const Action = { Connect: 0, Disconnect: 1, Manage: 2 };

const WildCardIntegrations = [
    LocationIntegrationName.CoverManager,
    LocationIntegrationName.iFood,
    LocationIntegrationName.VTEX,
];

const getRowsData = (
    account,
    company,
    onActionClick,
    anchor,
    handleOpenEl,
    handleCloseEl,
    hasMissingRequiredFields,
) => {
    const { t } = useTranslation();
    const history = useHistory();

    const getIntegrationsConnected = (type, profileType) => {
        const officialIntegrationConnected = account?.integrations.find(i => i.type === profileType)?.isActive;
        const integration = company?.integrations?.find(i => i.type === type);
        return { officialIntegrationConnected, integration };
    };

    const getStatus = integration => {
        switch (integration?.status) {
            case LocationIntegrationStatus.Disconnected:
                return { color: 'red', text: t('SettingsCompanies.disconnected') };
            case LocationIntegrationStatus.Unverified:
                return { color: 'red', text: t('common.unverified') };
            case LocationIntegrationStatus.Pending:
                return { color: 'yellow', text: t('common.inProgress') };
            case LocationIntegrationStatus.Connected:
                return { color: 'green', text: t('SettingsCompanies.connected') };
            default:
                return { color: '', text: t('SettingsCompanies.notConnected') };
        }
    };

    return [
        { name: 'Google', type: LocationIntegrationName.Google, profileType: ProfileName.GoogleOfficial },
        {
            name: 'Facebook & Instagram',
            type: LocationIntegrationName.Facebook,
            profileType: ProfileName.FacebookOfficial,
        },
        { name: 'Apple', type: LocationIntegrationName.Apple, profileType: ProfileName.Apple },
        { name: 'CoverManager', type: LocationIntegrationName.CoverManager, profileType: ProfileName.CoverManager },
        { name: 'iFood', type: LocationIntegrationName.iFood, profileType: ProfileName.iFood },
        { name: 'VTEX', type: LocationIntegrationName.VTEX, profileType: ProfileName.VTEX },
        {
            name: t('SettingsCompanies.searchPack'),
            type: LocationIntegrationName.SearchPack,
            pack: 'Apple, Bing, Foursquare, Amazon Alexa',
        },
        {
            name: t('SettingsCompanies.navigationPack'),
            type: LocationIntegrationName.NavigationPack,
            pack: 'Waze, Navmii, TomTom, Uber, HERE',
        },
        { name: t('SettingsCompanies.networkPack'), type: LocationIntegrationName.NetworkPack, pack: true },
    ]
        .filter(({ type, profileType }) => {
            if (account?.locale !== LOCALE.UNITED_STATES && type === LocationIntegrationName.NetworkPack) {
                return false;
            }
            if (!WildCardIntegrations.includes(type)) {
                return true;
            }
            const { integration, officialIntegrationConnected } = getIntegrationsConnected(type, profileType);
            return officialIntegrationConnected || integration;
        })
        .map(({ name, type, pack, profileType }, index) => {
            const { integration, officialIntegrationConnected } = getIntegrationsConnected(type, profileType);
            const isGoogleOrFb = type === LocationIntegrationName.Google || type === LocationIntegrationName.Facebook;
            const isDisconnected = integration?.status === LocationIntegrationStatus.Disconnected || !integration;

            const getMenu = () => {
                if (!integration) {
                    return [
                        {
                            id: 0,
                            title: t('buttons.connect'),
                            onClick:
                                !officialIntegrationConnected && !pack
                                    ? () => history.push(RoutePath.AccountSettingsIntegrations)
                                    : () => onActionClick(type, profileType, Action.Connect, integration?.id),
                            disabled: Boolean(pack && hasMissingRequiredFields),
                            tooltip:
                                pack && hasMissingRequiredFields
                                    ? t('SettingsCompanies.missingRequiredFields', { fields: hasMissingRequiredFields })
                                    : '',
                        },
                    ];
                } else {
                    return [
                        ...(isDisconnected
                            ? [
                                  {
                                      id: 0,
                                      title: t('buttons.connect'),
                                      onClick: () => history.push(RoutePath.AccountSettingsIntegrations),
                                  },
                              ]
                            : []),
                        ...(isGoogleOrFb
                            ? [
                                  {
                                      id: 0,
                                      title: t('buttons.manage'),
                                      onClick: () => onActionClick(type, profileType, Action.Manage, integration?.id),
                                  },
                              ]
                            : []),
                        {
                            id: 1,
                            title: t('buttons.disconnect'),
                            onClick: () => onActionClick(type, profileType, Action.Disconnect, integration?.id),
                        },
                    ];
                }
            };
            const { color, text } = getStatus(integration);
            return [
                {
                    component: (
                        <TableText variant="subtitle2" align="left">
                            {name}
                            {pack && typeof pack === 'string' && (
                                <IconButton sx={{ padding: '4px !important', marginLeft: '4px' }}>
                                    <Tooltip title={pack} placement="top" enterTouchDelay={0}>
                                        <HelpIcon sx={{ height: '18px !important', width: '18px !important' }} />
                                    </Tooltip>
                                </IconButton>
                            )}
                        </TableText>
                    ),
                },
                {
                    component: (
                        <TableText variant="body2" align="left">
                            <Point type={color} />
                            {text}
                        </TableText>
                    ),
                },
                {
                    component: (
                        <MoreMenu
                            id={index}
                            menu={getMenu()}
                            handleOpenEl={handleOpenEl}
                            handleCloseEl={handleCloseEl}
                            anchor={anchor}
                        />
                    ),
                },
            ];
        });
};

const ImplicitIntegrations = [
    LocationIntegrationName.Google,
    LocationIntegrationName.Facebook,
    LocationIntegrationName.CoverManager,
    LocationIntegrationName.iFood,
    LocationIntegrationName.VTEX,
];

const IntegrationsTable = ({ account, selectedCompany, setSelectedCompany }) => {
    const { t } = useTranslation();
    const [integration, setIntegration] = useState({ open: false, type: null, id: null });
    const [isDisconnectModalOpen, setDisconnectModalOpen] = useState(false);
    const [isProfileModalOpen, setProfileModalOpen] = useState(false);
    const [anchor, setAnchor] = useState({ el: null, id: null });
    const dispatch = useDispatch();

    const handleOpenEl = (event, id) => {
        event.stopPropagation();
        setAnchor({ id, el: event.currentTarget });
    };

    const handleCloseEl = () => setAnchor(prev => ({ ...prev, el: null }));

    const onIntegrationClick = async (integrationType, profileType, action, integrationId) => {
        handleCloseEl();
        switch (action) {
            case Action.Connect:
                if (!ImplicitIntegrations.includes(integrationType)) {
                    onConnectIntegrationExplicitly(integrationType);
                } else {
                    setIntegration(prev => ({ ...prev, open: true, type: integrationType, profileType }));
                }
                return;
            case Action.Disconnect:
                setIntegration(prev => ({ ...prev, type: integrationType, profileType, id: integrationId }));
                setDisconnectModalOpen(true);
                return;
            case Action.Manage:
                setIntegration(prev => ({ ...prev, type: integrationType, profileType, id: integrationId }));
                setProfileModalOpen(true);
                return;
            default:
                return;
        }
    };

    const IntegrationMapCb = {
        [LocationIntegrationName.Apple]: locationId => appleIntegrationService.editProfile(locationId),
        [LocationIntegrationName.NavigationPack]: locationId =>
            companyService.connectPackIntegration(locationId, 'navigation-pack'),
        [LocationIntegrationName.SearchPack]: locationId =>
            companyService.connectPackIntegration(locationId, 'search-pack'),
        [LocationIntegrationName.NetworkPack]: locationId =>
            companyService.connectPackIntegration(locationId, 'network-pack'),
    };

    const onConnectIntegrationExplicitly = async integrationType => {
        try {
            const updatedCompany = await IntegrationMapCb[integrationType](selectedCompany.companyId);
            await dispatch(accountActions.get());
            await dispatch(locationsProfilesActions.get(selectedCompany.companyId));
            setSelectedCompany(updatedCompany);
            dispatch(alertActions.success(t('SettingsCompanies.connectIntegrationMsg')));
        } catch (error) {
            handleApiError(error);
        }
    };

    const DataResetMachine = createProxyObject({
        [LocationIntegrationName.CoverManager]: coverManagerActions.reset,
        [LocationIntegrationName.FacebookOfficial]: facebookActions.reset,
        [LocationIntegrationName.GoogleOfficial]: googleActions.reset,
        [LocationIntegrationName.VTEX]: vtexActions.reset,
        default: () => ({ type: 'RESET_LOCATION_INTEGRATION' }),
    });

    const disconnectIntegration = async () => {
        try {
            const updatedCompany = await companyService.disconnectIntegration(
                selectedCompany.companyId,
                integration.id,
            );
            await dispatch(accountActions.get());
            setSelectedCompany(updatedCompany);
            await dispatch(locationsProfilesActions.get(selectedCompany.companyId));
            dispatch(DataResetMachine[integration.type]());
            if (isProfileModalOpen) {
                setProfileModalOpen(false);
            }
            setDisconnectModalOpen(false);
            setIntegration(prev => ({ ...prev, type: null, id: null }));
            dispatch(alertActions.success(t('SettingsCompanies.profileDisconnected')));
        } catch (error) {
            handleApiError(error);
        }
    };

    const onCloseDrawer = () => setIntegration(prev => ({ ...prev, open: false, type: null }));

    const onCloseProfileModal = () => setProfileModalOpen(false);

    const fields = [
        { name: t('SettingsCompanies.legalName'), key: ['legalName'] },
        { name: t('SettingsCompanies.website'), key: ['website'] },
        {
            name: t('SettingsCompanies.address'),
            key: ['addressLine1', 'city', 'country', 'postalCode'],
            condition: selectedCompany.hasBusinessAddress,
        },
        { name: t('SettingsCompanies.primaryPhone'), key: ['phoneNumber'] },
        {
            name: t('common.category', { count: 0 }),
            key: ['primaryCategory', 'secondaryCategories'],
            anyKeyValid: true,
        },
    ];
    const hasMissingRequiredFields = useMemo(() => {
        const missingFields = fields.filter(field => {
            if ('condition' in field && !field.condition) {
                return false;
            }
            const checkMethod = field.anyKeyValid ? 'every' : 'some';
            return field.key[checkMethod](key => {
                const value = selectedCompany[key];
                if (value === null || value === undefined) {
                    return true;
                }
                if (Array.isArray(value) && value.length === 0) {
                    return true;
                }
                return false;
            });
        });
        return missingFields.map(field => field.name).join(', ');
    }, [selectedCompany]);

    return (
        <>
            <Container>
                <Typography variant="subtitle1" sx={{ color: 'rgba(0, 0, 0, 0.87)', marginBottom: '16px' }}>
                    {t('SettingsCompanies.integrations')}
                </Typography>
                <TableContainer>
                    <StyledTable
                        columnNames={getColumnNames(t)}
                        rows={getRowsData(
                            account,
                            selectedCompany,
                            onIntegrationClick,
                            anchor,
                            handleOpenEl,
                            handleCloseEl,
                            hasMissingRequiredFields,
                        )}
                    />
                </TableContainer>
            </Container>
            {integration.open ? (
                <IntegrationDrawer
                    isOpen={integration.open}
                    onClose={onCloseDrawer}
                    onConnect={onCloseDrawer}
                    integrationType={integration.type}
                    profileType={integration.profileType}
                    selectedCompany={selectedCompany}
                    setSelectedCompany={setSelectedCompany}
                />
            ) : null}
            {isDisconnectModalOpen && (
                <ModalConfirm
                    isOpen={isDisconnectModalOpen}
                    handleClose={() => setDisconnectModalOpen(false)}
                    title={t('SettingsCompanies.disconnectIntegration')}
                    description={t('SettingsCompanies.disconnectIntegrationMessage')}
                    onPrimaryAction={disconnectIntegration}
                    primaryActionText={t('buttons.disconnect')}
                    primaryActionType="danger"
                />
            )}
            {isProfileModalOpen && (
                <ManageProfileModal
                    integrationType={integration.type}
                    open={isProfileModalOpen}
                    onCloseModal={onCloseProfileModal}
                    selectedCompany={selectedCompany}
                    setDisconnectModalOpen={setDisconnectModalOpen}
                />
            )}
        </>
    );
};

export default IntegrationsTable;
